import type { IUpdateSchoolProfilePayload } from '@/types/payload';

import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { IconEdit, IconCamera } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import PhoneNumberInput from '@/components/phoneInput';
import SettingsSkeleton from '@/components/SkeletonLoader/SettingsSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import {
  profileSettingsSchema,
  type TProfileSettingsForm,
} from '@/schema/profileSettingsSchema';
import { uploadImage } from '@/service/parentSettingsServices';
import {
  getSchoolProfile,
  updateSchoolProfile,
} from '@/service/settingsService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import useUser from '@/store/useUser';
import { getAssetUrl } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

const DEFAULT_PROFILE: TProfileSettingsForm = {
  schoolName: '',
  primaryContact: '',
  primaryContactCode: '+965',
  email: '',
  phone: '',
  countryCode: '+965',
};

interface ISchoolProfileData {
  name?: string;
  primary_contact?: string;
  primary_contact_name?: string;
  email?: string;
  phone_code?: string;
  phone_number?: string;
  asset_id?: string;
  profile_pic?: string;
  primary_contact_code?: string | null;
  primary_contact_number?: string | null;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-2">
    <span className="text-sm text-neutral-secondary font-medium">{label}</span>
    <span className="text-base font-semibold text-neutral-primary">
      {value}
    </span>
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

const ProfileSettings = () => {
  const { formatText } = useFormatText();
  const { user, setUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();
  const [profileData, setProfileData] = useState<ISchoolProfileData | null>(
    null
  );
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TProfileSettingsForm>({
    resolver: zodResolver(profileSettingsSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_PROFILE,
  });

  const selectedCountryCode = watch('countryCode');

  const mapApiToForm = useCallback((data: ISchoolProfileData) => {
    return {
      schoolName: data.name || '',
      primaryContact: data.primary_contact_number || '',
      primaryContactCode: data.primary_contact_code || '+965',
      email: data.email || '',
      phone: data.phone_number || '',
      countryCode: data.phone_code || '+965',
    };
  }, []);

  const fetchSchoolProfile = useCallback(async () => {
    setScreenLoading(true);
    try {
      const res = await getSchoolProfile();
      if (res?.status && res?.data) {
        const data = res.data as ISchoolProfileData;
        setProfileData(data);
        reset(mapApiToForm(data));
        setProfileImage(getAssetUrl(data?.profile_pic) || null);
        setAssetId(data.asset_id || '');
        setUser({
          ...user,
          name: data.name || user.name,
          profile_pic: getAssetUrl(data?.profile_pic),
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setScreenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapApiToForm, reset]);

  useEffect(() => {
    fetchSchoolProfile();
  }, [fetchSchoolProfile]);

  const handleEdit = useCallback(() => {
    reset(mapApiToForm(profileData || {}));
    setIsEditing(true);
  }, [mapApiToForm, profileData, reset]);

  const handleCancel = useCallback(() => {
    reset(mapApiToForm(profileData || {}));
    setProfileImage(getAssetUrl(profileData?.profile_pic) || null);
    setAssetId(profileData?.asset_id || '');
    setIsEditing(false);
  }, [mapApiToForm, profileData, reset]);

  const onSubmit = useCallback(
    async (data: TProfileSettingsForm) => {
      try {
        const payload: IUpdateSchoolProfilePayload = {
          name: data.schoolName,
          phone_code: data.countryCode || '+965',
          phone_number: data.phone,
          primary_contact_code: data.primaryContactCode || '+965',
          primary_contact_number: data.primaryContact,
          ...(assetId && { asset_id: assetId }),
        };

        const response = await updateSchoolProfile(payload);

        if (response?.status) {
          setUser({
            ...user,
            name: payload.name,
            profile_pic: profileImage || '',
          });
          enqueueSnackbar(response?.message, { variant: 'success' });
          setIsEditing(false);
          await fetchSchoolProfile();
        } else {
          enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } catch (error) {
        console.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetId, fetchSchoolProfile]
  );

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please upload an image file', { variant: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('Image size should be less than 5MB', {
          variant: 'error',
        });
        return;
      }

      // Show local preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to server
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'teacher');
        const response = await uploadImage(formData);
        if (response?.status && response?.data?.id) {
          setAssetId(response.data.id);
        } else {
          setProfileImage(getAssetUrl(profileData?.profile_pic) || null);
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        setProfileImage(getAssetUrl(profileData?.profile_pic) || null);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [profileData?.profile_pic]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ... (existing imports)

  // Inside component ...

  if (isScreenLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="max-w-217 w-full" data-testid="profile-settings-container">
      {/* Section Title */}
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.profile.title')}
      </h2>

      {/* Card */}
      <div
        className="mt-6 border border-neutral-light-200 rounded-xl"
        data-testid="profile-settings-card"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6">
            {/* School Information Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-neutral-primary">
                {formatText('settings.profile.school-information')}
              </h3>

              {/* Edit / Save+Cancel toggle */}
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center gap-3 transition-all duration-300 ease-in-out ${
                    isEditing
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-1 pointer-events-none absolute'
                  }`}
                  data-testid="edit-actions"
                >
                  <Button
                    type="button"
                    variant="outline"
                    label={formatText('common.actions.cancel')}
                    onClick={handleCancel}
                    className="px-4! py-2! text-sm!"
                    dataTestId="cancel-edit-button"
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    label={formatText('common.actions.save-changes')}
                    className="px-4! py-2! text-sm!"
                    loading={isUploading}
                    dataTestId="save-changes-button"
                  />
                </div>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    !isEditing
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-1 pointer-events-none absolute'
                  }`}
                  data-testid="view-actions"
                >
                  <Button
                    variant="unstyled"
                    onClick={handleEdit}
                    className="p-2.5 rounded-lg border border-neutral-light-200 hover:bg-neutral-light-50 transition-colors cursor-pointer"
                    icon={
                      <IconEdit size={20} className="text-neutral-secondary" />
                    }
                    dataTestId="edit-profile-button"
                  />
                </div>
              </div>
            </div>

            {/* School Info Content */}
            <div
              className="mt-4 flex flex-col gap-6"
              data-testid="profile-info-content"
            >
              {/* Avatar & Name */}
              <div className="flex flex-col gap-4">
                <div
                  className="relative w-25 h-25 group"
                  data-testid="profile-image-container"
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-25 h-25 rounded-full object-cover"
                      data-testid="profile-image"
                    />
                  ) : (
                    <UserAvatarList
                      user={profileData?.name || '-'}
                      className="w-25! h-25! min-w-25! text-3xl!"
                      dataTestId="profile-avatar"
                    />
                  )}
                  {isEditing && (
                    <>
                      <Button
                        variant="unstyled"
                        onClick={handleUploadClick}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        icon={<IconCamera size={32} className="text-white" />}
                        disabled={isUploading}
                        dataTestId="upload-image-button"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </>
                  )}
                </div>

                {/* School name: read-only or input */}
                <div
                  className="min-h-10 flex items-end"
                  data-testid="school-name-container"
                >
                  {isEditing ? (
                    <Input
                      name="schoolName"
                      label={formatText('settings.profile.school-name')}
                      register={register}
                      error={
                        errors.schoolName?.message
                          ? formatText(errors.schoolName.message)
                          : undefined
                      }
                      required
                      autoFocus
                      className="w-full max-w-xs animate-fade-in"
                      dataTestId="school-name-input"
                    />
                  ) : (
                    <h4
                      className="text-2xl font-bold text-neutral-primary leading-8 tracking-tight"
                      data-testid="school-name-display"
                    >
                      {profileData?.name || '-'}
                    </h4>
                  )}
                </div>
              </div>

              {/* Contact Details */}
              <div
                className="grid grid-cols-3 gap-8"
                data-testid="contact-details-grid"
              >
                {/* Primary Contact */}
                <div
                  className="min-h-15"
                  data-testid="primary-contact-container"
                >
                  {isEditing ? (
                    <PhoneNumberInput
                      name="primaryContact"
                      label={formatText('settings.profile.primary-contact')}
                      register={register}
                      setValue={setValue}
                      countryFlag
                      error={
                        errors.primaryContact?.message
                          ? formatText(errors.primaryContact.message)
                          : undefined
                      }
                      countrySelected={watch('primaryContactCode') || '+965'}
                      countryCodeName="primaryContactCode"
                      required
                      dataTestId="primary-contact-input"
                    />
                  ) : (
                    <div data-testid="primary-contact-display">
                      <ReadOnlyField
                        label={formatText('settings.profile.primary-contact')}
                        value={`${profileData?.primary_contact_code || ''} ${profileData?.primary_contact_number || '-'}`.trim()}
                      />
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="min-h-15" data-testid="email-display">
                  <ReadOnlyField
                    label={formatText('settings.profile.email-id')}
                    value={profileData?.email || '-'}
                  />
                </div>

                {/* Phone */}
                <div className="min-h-15" data-testid="phone-container">
                  {isEditing ? (
                    <PhoneNumberInput
                      name="phone"
                      label={formatText('settings.profile.phone-number')}
                      register={register}
                      setValue={setValue}
                      countryFlag
                      error={
                        errors.phone?.message
                          ? formatText(errors.phone.message)
                          : undefined
                      }
                      countrySelected={selectedCountryCode || '+965'}
                      required
                      dataTestId="phone-input"
                    />
                  ) : (
                    <div data-testid="phone-display">
                      <ReadOnlyField
                        label={formatText('settings.profile.phone-number')}
                        value={`${profileData?.phone_code || ''} ${profileData?.phone_number || '-'}`.trim()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings;
